import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useApp } from '../context/useApp';
import { useAuth } from '../context/useAuth';
import { useLanguage } from '../i18n';
import type { View } from './AppLayout';
import {
  Home,
  BookOpen,
  Settings,
  FileText,
  Zap,
  Crown,
  ArrowUpRight,
  Shield,
  MessageCircleQuestion,
  Menu,
  X,
  ChevronRight,
  Building2,
} from 'lucide-react';

interface SidebarProps {
  currentView: View;
  onChangeView: (view: View) => void;
  onOpenUpgrade?: () => void;
  onOpenInterview?: () => void;
}

const iconMap: Partial<Record<View, any>> = {
  dashboard: Home,
  organizations: Building2,
  framework: BookOpen,
  settings: Settings,
  project: FileText,
  organization: Building2,
  interview: MessageCircleQuestion,
};

export default function Sidebar({
  currentView,
  onChangeView,
  onOpenUpgrade,
  onOpenInterview,
}: SidebarProps) {
  const { activeProjectId, activeInterviewSessionId } = useApp();
  const { user, isAdmin } = useAuth();
  const { t } = useLanguage();
  const [drawerOpen, setDrawerOpen] = useState(false);

  const used = user?.generations_used ?? 0;
  const limit = user?.generations_limit ?? 0;
  const pct = limit > 0 ? Math.min(100, Math.round((used / limit) * 100)) : 0;

  const navItems: { view: Exclude<View, 'project' | 'organization' | 'upgrade' | 'advance'>; label: string }[] = [
    { view: 'dashboard', label: t.nav.dashboard },
    { view: 'organizations', label: t.nav.organizations },
    { view: 'framework', label: t.nav.framework },
    { view: 'settings', label: t.nav.settings },
  ];

  useEffect(() => {
    if (!drawerOpen) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setDrawerOpen(false);
    };
    document.addEventListener('keydown', onKey);
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = prevOverflow;
    };
  }, [drawerOpen]);

  const closeDrawer = () => setDrawerOpen(false);
  const handleNav = (view: View) => {
    onChangeView(view);
    closeDrawer();
  };
  const handleOpenInterview = () => {
    onOpenInterview?.();
    closeDrawer();
  };

  return (
    <>
      {/* Mobile header */}
      <header className="sm:hidden sticky top-0 z-30 bg-ink text-white border-b border-cloud/10 safe-top">
        <div className="flex items-center justify-between px-4 h-14">
          <button
            type="button"
            onClick={() => setDrawerOpen(true)}
            className="p-2 -ml-2 text-white hover:bg-white/10 min-w-[44px] min-h-[44px] flex items-center justify-center"
            aria-label="Open menu"
            aria-expanded={drawerOpen}
          >
            <Menu size={20} />
          </button>
          <div className="flex items-center gap-2 min-w-0">
            <div className="w-7 h-7 bg-red-500 flex items-center justify-center shrink-0">
              <Zap size={16} className="text-white" strokeWidth={2.5} />
            </div>
            <span className="font-display font-bold text-base tracking-tight truncate">
              {t.brand}
            </span>
          </div>
          {activeInterviewSessionId && onOpenInterview ? (
            <button
              type="button"
              onClick={handleOpenInterview}
              className="p-2 -mr-2 text-red-400 hover:bg-white/10 min-w-[44px] min-h-[44px] flex items-center justify-center"
              aria-label={t.interview.sessionTitle}
            >
              <MessageCircleQuestion size={20} />
            </button>
          ) : (
            <span className="w-10" aria-hidden="true" />
          )}
        </div>
      </header>

      {/* Desktop rail */}
      <aside className="hidden sm:flex w-64 bg-ink text-white flex-col border-r border-cloud/10">
        <div className="p-6 border-b border-cloud/10">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 bg-red-500 flex items-center justify-center">
              <Zap size={20} className="text-white" strokeWidth={2.5} />
            </div>
            <span className="font-display font-bold text-xl tracking-tight">{t.brand}</span>
          </div>
        </div>

        <nav className="flex-1 p-4 space-y-1">
          {navItems.map(({ view, label }) => {
            const Icon = iconMap[view];
            return (
              <button
                key={view}
                onClick={() => onChangeView(view)}
                className={`w-full flex items-center gap-3 px-4 py-3 text-left transition-colors ${
                  currentView === view
                    ? 'bg-white/10 text-white border-l-2 border-red-500'
                    : 'text-cloud hover:bg-white/5 hover:text-white border-l-2 border-transparent'
                }`}
              >
                <Icon size={18} />
                <span className="font-medium">{label}</span>
              </button>
            );
          })}

          {activeInterviewSessionId && onOpenInterview && (
            <button
              onClick={onOpenInterview}
              className={`w-full flex items-center gap-3 px-4 py-3 text-left transition-colors ${
                currentView === 'interview'
                  ? 'bg-white/10 text-white border-l-2 border-red-500'
                  : 'text-cloud hover:bg-white/5 hover:text-white border-l-2 border-transparent'
              }`}
            >
              <MessageCircleQuestion size={18} />
              <span className="font-medium">{t.interview.sessionTitle}</span>
            </button>
          )}

          {activeProjectId && (
            <button
              onClick={() => onChangeView('project')}
              className={`w-full flex items-center gap-3 px-4 py-3 text-left transition-colors mt-4 ${
                currentView === 'project'
                  ? 'bg-white/10 text-white border-l-2 border-red-500'
                  : 'text-cloud hover:bg-white/5 hover:text-white border-l-2 border-transparent'
              }`}
            >
              <FileText size={18} />
              <span className="font-medium">{t.nav.activeProject}</span>
            </button>
          )}
        </nav>

        <div className="p-4 border-t border-cloud/10 space-y-3">
          {user && (
            <div className="bg-charcoal border border-cloud/10 p-4 space-y-3">
              <div className="flex items-center justify-between">
                <span className="text-xs font-semibold uppercase tracking-wider text-ash">{t.settings.currentPlan}</span>
                <span className="text-xs font-semibold px-2 py-0.5 bg-red-500/10 text-red-400">
                  {t.plans[user.plan] || user.plan}
                </span>
              </div>
              <div>
                <div className="flex items-center justify-between text-xs mb-1.5">
                  <span className="text-cloud">{t.settings.aiRequests}</span>
                  <span className="text-white font-medium">
                    {used.toLocaleString()} / {limit.toLocaleString()}
                  </span>
                </div>
                <div className="h-1.5 bg-charcoal border border-cloud/20 overflow-hidden">
                  <div className="h-full bg-red-500 transition-all duration-300" style={{ width: `${pct}%` }} />
                </div>
              </div>

              {onOpenUpgrade && (
                <button
                  onClick={onOpenUpgrade}
                  disabled={user.plan === 'scale'}
                  className="w-full flex items-center justify-center gap-2 px-3 py-2 text-sm bg-red-500 hover:bg-red-600 disabled:bg-red-900/30 disabled:text-red-300 text-white font-medium transition-colors"
                >
                  <Crown size={16} />
                  {user.plan === 'scale' ? t.plans.scale : t.project.upgradeNow}
                  {user.plan !== 'scale' && <ArrowUpRight size={14} />}
                </button>
              )}

              {isAdmin && (
                <Link
                  to="/admin"
                  className="w-full flex items-center justify-center gap-2 px-3 py-2 text-sm text-cloud hover:text-white hover:bg-white/5 border border-cloud/20 transition-colors"
                >
                  <Shield size={16} />
                  {t.admin.title}
                </Link>
              )}
            </div>
          )}
        </div>
      </aside>

      {/* Mobile drawer */}
      {drawerOpen && (
        <div className="sm:hidden fixed inset-0 z-50 flex">
          <button
            type="button"
            className="absolute inset-0 bg-black/60"
            aria-label="Close menu"
            onClick={closeDrawer}
          />
          <div className="relative ml-0 w-[88%] max-w-sm h-full bg-ink text-white shadow-2xl flex flex-col safe-top safe-bottom animate-fade-in">
            <div className="px-5 py-4 flex items-center justify-between border-b border-cloud/10">
              <div className="flex items-center gap-2 min-w-0">
                <div className="w-8 h-8 bg-red-500 flex items-center justify-center shrink-0">
                  <Zap size={18} className="text-white" strokeWidth={2.5} />
                </div>
                <span className="font-display font-bold text-lg tracking-tight">{t.brand}</span>
              </div>
              <button
                type="button"
                onClick={closeDrawer}
                className="p-2 -mr-2 text-cloud hover:text-white hover:bg-white/10 min-w-[44px] min-h-[44px] flex items-center justify-center"
                aria-label="Close menu"
              >
                <X size={20} />
              </button>
            </div>

            <nav className="flex-1 overflow-y-auto p-4 space-y-1">
              {navItems.map(({ view, label }) => {
                const Icon = iconMap[view];
                const active = currentView === view;
                return (
                  <button
                    key={view}
                    onClick={() => handleNav(view)}
                    className={`w-full flex items-center gap-4 px-4 min-h-[48px] text-left text-base transition-colors ${
                      active
                        ? 'bg-white/10 text-white border-l-2 border-red-500'
                        : 'text-cloud hover:bg-white/5 hover:text-white border-l-2 border-transparent'
                    }`}
                  >
                    <Icon size={20} />
                    <span className="font-medium flex-1">{label}</span>
                    <ChevronRight size={18} className="text-ash" />
                  </button>
                );
              })}

              {activeInterviewSessionId && onOpenInterview && (
                <button
                  onClick={handleOpenInterview}
                  className={`w-full flex items-center gap-4 px-4 min-h-[48px] text-left text-base transition-colors ${
                    currentView === 'interview'
                      ? 'bg-white/10 text-white border-l-2 border-red-500'
                      : 'text-cloud hover:bg-white/5 hover:text-white border-l-2 border-transparent'
                  }`}
                >
                  <MessageCircleQuestion size={20} />
                  <span className="font-medium flex-1">{t.interview.sessionTitle}</span>
                  <ChevronRight size={18} className="text-ash" />
                </button>
              )}

              {activeProjectId && (
                <button
                  onClick={() => handleNav('project')}
                  className={`w-full flex items-center gap-4 px-4 min-h-[48px] text-left text-base mt-4 transition-colors ${
                    currentView === 'project'
                      ? 'bg-white/10 text-white border-l-2 border-red-500'
                      : 'text-cloud hover:bg-white/5 hover:text-white border-l-2 border-transparent'
                  }`}
                >
                  <FileText size={20} />
                  <span className="font-medium flex-1">{t.nav.activeProject}</span>
                  <ChevronRight size={18} className="text-ash" />
                </button>
              )}
            </nav>

            {user && (
              <div className="p-4 border-t border-cloud/10 space-y-3">
                <div className="flex items-center justify-between">
                  <span className="text-xs font-semibold uppercase tracking-wider text-ash">{t.settings.currentPlan}</span>
                  <span className="text-xs font-semibold px-2 py-0.5 bg-red-500/10 text-red-400">
                    {t.plans[user.plan] || user.plan}
                  </span>
                </div>
                <div>
                  <div className="flex items-center justify-between text-xs mb-1.5">
                    <span className="text-cloud">{t.settings.aiRequests}</span>
                    <span className="text-white font-medium">
                      {used.toLocaleString()} / {limit.toLocaleString()}
                    </span>
                  </div>
                  <div className="h-1.5 bg-charcoal border border-cloud/20 overflow-hidden">
                    <div className="h-full bg-red-500 transition-all duration-300" style={{ width: `${pct}%` }} />
                  </div>
                </div>

                {onOpenUpgrade && (
                  <button
                    onClick={() => {
                      onOpenUpgrade();
                      closeDrawer();
                    }}
                    disabled={user.plan === 'scale'}
                    className="w-full flex items-center justify-center gap-2 px-3 min-h-[44px] text-sm bg-red-500 hover:bg-red-600 disabled:bg-red-900/30 disabled:text-red-300 text-white font-medium transition-colors"
                  >
                    <Crown size={16} />
                    {user.plan === 'scale' ? t.plans.scale : t.project.upgradeNow}
                    {user.plan !== 'scale' && <ArrowUpRight size={14} />}
                  </button>
                )}

                {isAdmin && (
                  <Link
                    to="/admin"
                    onClick={closeDrawer}
                    className="w-full flex items-center justify-center gap-2 px-3 min-h-[44px] text-sm text-cloud hover:text-white hover:bg-white/5 border border-cloud/20 transition-colors"
                  >
                    <Shield size={16} />
                    {t.admin.title}
                  </Link>
                )}
              </div>
            )}
          </div>
        </div>
      )}
    </>
  );
}
